import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Link as MuiLink,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { activityApi } from '@/api/activityApi'
import { displayErrorMessage, getErrorMessage } from '@/utils/error'
import type { ActivityFeedGroup, ActivityType } from '@/types/activity'

interface ActivityTimelineProps {
  /** Чей журнал; без него — свой */
  userId?: number
  /** Сводная лента учителя по своим ученикам */
  scope?: 'students'
  /** Детализация одного дня (модалка) */
  date?: string
  types?: ActivityType[]
  /** Показывать автора события (лента учителя) */
  showUserName?: boolean
  perPage?: number
  /** Внешний триггер перезагрузки (например, realtime-обновление виджета) */
  reloadKey?: number
  emptyText?: string
}

/**
 * Вертикальная лента активности со сгруппированными по дню и типу событиями.
 * Следующая страница подгружается по кнопке «Загрузить ещё» (не по скроллу).
 *
 * Группа одного дня и типа может прийти в двух соседних страницах — склеиваем
 * их по ключу `date|type`, как описано в контракте эндпоинта.
 */
export function ActivityTimeline({
  userId,
  scope,
  date,
  types,
  showUserName = false,
  perPage = 20,
  reloadKey = 0,
  emptyText,
}: ActivityTimelineProps) {
  const { t } = useTranslation()

  const [groups, setGroups] = useState<ActivityFeedGroup[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(true)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Гонки: ответ устаревшего фильтра не должен перезаписать актуальный список
  const requestIdRef = useRef(0)

  const typesKey = useMemo(() => (types ?? []).join(','), [types])

  const mergeGroups = useCallback(
    (previous: ActivityFeedGroup[], incoming: ActivityFeedGroup[]): ActivityFeedGroup[] => {
      const merged = [...previous]
      for (const group of incoming) {
        const index = merged.findIndex(
          (item) => item.date === group.date && item.type === group.type,
        )
        if (index === -1) {
          merged.push(group)
          continue
        }
        merged[index] = {
          ...merged[index],
          count: merged[index].count + group.count,
          items: [...merged[index].items, ...group.items].slice(0, 3),
        }
      }
      return merged
    },
    [],
  )

  const load = useCallback(
    async (nextCursor: string | null, replace: boolean) => {
      const requestId = ++requestIdRef.current
      if (replace) {
        setLoading(true)
      } else {
        setLoadingMore(true)
      }

      try {
        const response = await activityApi.feed({
          userId,
          scope,
          date,
          types,
          perPage,
          cursor: nextCursor,
        })

        if (requestId !== requestIdRef.current) return

        setGroups((previous) =>
          replace ? response.groups : mergeGroups(previous, response.groups),
        )
        setCursor(response.next_cursor)
        setHasMore(response.has_more)
        setError(null)
      } catch (caught) {
        if (requestId !== requestIdRef.current) return
        setError(displayErrorMessage(getErrorMessage(caught), t('common.error')))
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false)
          setLoadingMore(false)
        }
      }
    },
    [userId, scope, date, types, perPage, mergeGroups, t],
  )

  // Сброс и перезагрузка при смене фильтров/пользователя/дня
  useEffect(() => {
    setGroups([])
    setCursor(null)
    setHasMore(true)
    void load(null, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, scope, date, typesKey, perPage, reloadKey])

  if (loading) {
    return (
      <Stack spacing={1.5}>
        {[0, 1, 2].map((index) => (
          <Box key={index} sx={{ display: 'flex', gap: 1.5 }}>
            <Skeleton variant="circular" width={28} height={28} />
            <Box sx={{ flexGrow: 1 }}>
              <Skeleton variant="text" width="60%" />
              <Skeleton variant="text" width="40%" />
            </Box>
          </Box>
        ))}
      </Stack>
    )
  }

  // Ошибка подгрузки не должна стирать уже показанные группы
  if (error && groups.length === 0) {
    return <Alert severity="error">{error}</Alert>
  }

  if (groups.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyText ?? t('activity.empty')}
      </Typography>
    )
  }

  return (
    <Box>
      <Stack spacing={0}>
        {groups.map((group, index) => (
          <Box
            key={`${group.date}|${group.type}`}
            sx={{
              display: 'flex',
              gap: 1.5,
              pb: 2,
              position: 'relative',
              // Вертикальная линия таймлайна между точками
              '&::before':
                index < groups.length - 1
                  ? {
                      content: '""',
                      position: 'absolute',
                      left: 13,
                      top: 28,
                      bottom: 0,
                      width: '2px',
                      backgroundColor: 'divider',
                    }
                  : undefined,
            }}
          >
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                flexShrink: 0,
                backgroundColor: 'primary.main',
                opacity: 0.15,
                zIndex: 1,
              }}
            />
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {group.title}
                </Typography>
                {group.count > 1 && <Chip size="small" label={`×${group.count}`} />}
                <Typography variant="caption" color="text.secondary">
                  {dayjs(group.occurred_at).format('DD.MM.YYYY HH:mm')}
                </Typography>
              </Box>

              <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                {group.items.map((item) => (
                  <Box
                    key={item.id}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}
                  >
                    {showUserName && item.user && (
                      <>
                        <Avatar sx={{ width: 18, height: 18, fontSize: 10 }}>
                          {item.user.name.charAt(0)}
                        </Avatar>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {item.user.name}
                        </Typography>
                      </>
                    )}
                    {item.subtitle && (
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {item.subtitle}
                      </Typography>
                    )}
                    {item.link && (
                      <MuiLink
                        component={RouterLink}
                        to={item.link}
                        variant="caption"
                        underline="hover"
                      >
                        {t('activity.open')}
                      </MuiLink>
                    )}
                  </Box>
                ))}
                {group.count > group.items.length && (
                  <Typography variant="caption" color="text.secondary">
                    {t('activity.andMore', { count: group.count - group.items.length })}
                  </Typography>
                )}
              </Stack>
            </Box>
          </Box>
        ))}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {hasMore && cursor !== null && (
        <Button
          fullWidth
          size="small"
          variant="outlined"
          onClick={() => void load(cursor, false)}
          disabled={loadingMore}
          sx={{ mt: 1 }}
        >
          {t('activity.loadMore')}
        </Button>
      )}
    </Box>
  )
}
